import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Eye, Paperclip } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { SkeletonTable } from '../components/ui/Skeleton';
import { EmptyInvoices, ErrorState } from '../components/ui/EmptyState';
import AddInvoiceModal from '../components/invoices/AddInvoiceModal';
import EditInvoiceModal from '../components/invoices/EditInvoiceModal';
import DeleteInvoiceModal from '../components/invoices/DeleteInvoiceModal';
import ViewInvoiceModal from '../components/invoices/ViewInvoiceModal';
import FilePreviewModal from '../components/ui/FilePreviewModal';
import { formatDate, formatCurrency } from '../utils/dateUtils';
import { useAllInvoices } from '../hooks/useInvoices';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';
import { useCurrency } from '../context/CurrencyContext';
import { getCreatedByLabelKey } from '../utils/invoiceCreatedBy';

const statusStyles = {
    paid: 'bg-status-paid-bg text-status-paid',
    pending: 'bg-status-pending-bg text-status-pending',
    overdue: 'bg-status-overdue-bg text-status-overdue',
    upcoming: 'bg-accent/10 text-accent',
};

const DEFAULT_INVOICE_FILTERS = { status: 'all', limit: 100, date_from: '', date_to: '' };

const Invoices = () => {
    const [filters, setFilters] = useState(DEFAULT_INVOICE_FILTERS);
    const { t } = useLanguage();
    const { currency } = useCurrency();
    const {
        data,
        isLoading: loading,
        error,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useAllInvoices(filters);
    const invoices = data?.pages.flatMap((p) => p.items) ?? [];

    // Modal states
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    // Standalone file preview (from paperclip click)
    const [previewFileUid, setPreviewFileUid] = useState(null);
    const [previewFileLabel, setPreviewFileLabel] = useState('');

    const handleView = (invoice) => {
        setSelectedInvoice(invoice);
        setIsViewModalOpen(true);
    };

    const handleEdit = (invoice) => {
        setSelectedInvoice(invoice);
        setIsEditModalOpen(true);
    };

    const handleDelete = (invoice) => {
        setSelectedInvoice(invoice);
        setIsDeleteModalOpen(true);
    };

    const handleFilePreview = (fileUid, label) => {
        setPreviewFileUid(fileUid);
        setPreviewFileLabel(label);
    };

    const getInvoiceStatus = (invoice) => {
        if (invoice.is_paid) return 'paid';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(invoice.due_date);
        dueDate.setHours(0, 0, 0, 0);
        if (dueDate < today) return 'overdue';
        const threeDaysFromNow = new Date(today);
        threeDaysFromNow.setDate(today.getDate() + 3);
        if (dueDate <= threeDaysFromNow) return 'pending';
        return 'upcoming';
    };

    const amountColor = (status) => {
        if (status === 'paid') return 'text-status-paid';
        if (status === 'overdue') return 'text-status-overdue';
        if (status === 'pending') return 'text-status-pending';
        return 'text-text-primary';
    };

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-text-primary">
                        {t(KEYS.INVOICES_TITLE)}
                    </h1>
                    <p className="text-sm text-text-muted mt-0.5">
                        {t(KEYS.INVOICES_SUBTITLE)}
                    </p>
                </div>
                <Button 
                    variant="primary" 
                    size="small"
                    onClick={() => setIsAddModalOpen(true)}
                    className="gap-2"
                >
                    <Plus className="w-3.5 h-3.5" />
                    {t(KEYS.INVOICES_ADD)}
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    className="text-xs bg-surface-overlay border border-surface-border rounded-md px-2.5 py-1.5 text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                >
                    <option value="all">{t(KEYS.INVOICES_FILTER_ALL)}</option>
                    <option value="unpaid">{t(KEYS.INVOICES_FILTER_UNPAID)}</option>
                    <option value="paid">{t(KEYS.INVOICES_FILTER_PAID)}</option>
                    <option value="overdue">{t(KEYS.INVOICES_FILTER_OVERDUE)}</option>
                </select>
                <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                    aria-label="From date"
                    className="text-xs bg-surface-overlay border border-surface-border rounded-md px-2.5 py-1.5 text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
                />
                <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                    aria-label="To date"
                    className="text-xs bg-surface-overlay border border-surface-border rounded-md px-2.5 py-1.5 text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
                />
                {(filters.status !== DEFAULT_INVOICE_FILTERS.status || filters.date_from || filters.date_to) && (
                    <button
                        onClick={() => setFilters(DEFAULT_INVOICE_FILTERS)}
                        className="text-xs text-text-muted hover:text-accent transition-colors"
                    >
                        {t(KEYS.INVOICES_RESET_FILTERS)}
                    </button>
                )}
            </div>

            {/* Content */}
            {loading ? (
                <SkeletonTable rows={5} columns={6} />
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.INVOICES_ERROR_LOAD)} onRetry={refetch} />
            ) : invoices.length > 0 ? (
                <>
                <Card padding="none" className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-surface-border">
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.INVOICES_COL_NAME)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.INVOICES_COL_SERVICE)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden md:table-cell">
                                        {t(KEYS.INVOICES_COL_ISSUED)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.INVOICES_COL_DUE)}
                                    </th>
                                    <th className="px-4 py-2.5 text-right text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.INVOICES_COL_AMOUNT)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.INVOICES_COL_STATUS)}
                                    </th>
                                    <th className="px-4 py-2.5 w-20"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-border">
                                {invoices.map((invoice, index) => {
                                    const status = getInvoiceStatus(invoice);
                                    return (
                                        <tr 
                                            key={invoice.uid} 
                                            className="hover:bg-surface-overlay transition-colors group animate-appear"
                                            style={{ animationDelay: `${index * 30}ms` }}
                                        >
                                            <td className="px-4 py-2.5 text-sm text-text-primary">
                                                <div className="flex items-center gap-1.5">
                                                    <span>{invoice.name || '—'}</span>
                                                    {invoice.fileUid && (
                                                        <button
                                                            onClick={() => handleFilePreview(invoice.fileUid, invoice.name ? `${invoice.name} — Invoice` : 'Invoice File')}
                                                            className="p-0.5 text-text-muted hover:text-accent rounded transition-colors flex-shrink-0"
                                                            title="View invoice file"
                                                        >
                                                            <Paperclip className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                    {invoice.paymentProofFileUid && (
                                                        <button
                                                            onClick={() => handleFilePreview(invoice.paymentProofFileUid, invoice.name ? `${invoice.name} — Payment Proof` : 'Payment Proof')}
                                                            className="p-0.5 text-text-muted hover:text-status-paid rounded transition-colors flex-shrink-0"
                                                            title="View payment proof"
                                                        >
                                                            <Paperclip className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="mt-1">
                                                    <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider bg-surface-overlay text-text-muted">
                                                        {t(getCreatedByLabelKey(invoice.created_by))}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2.5 text-sm text-text-primary">
                                                {invoice.serviceName || t(KEYS.COMMON_UNKNOWN_SERVICE)}
                                            </td>
                                            <td className="px-4 py-2.5 text-xs font-mono text-text-muted hidden md:table-cell">
                                                {formatDate(invoice.issue_date)}
                                            </td>
                                            <td className="px-4 py-2.5 text-xs font-mono text-text-muted">
                                                {formatDate(invoice.due_date)}
                                            </td>
                                            <td className={`px-4 py-2.5 text-sm font-mono font-medium text-right ${amountColor(status)}`}>
                                                {formatCurrency(invoice.amount, currency)}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${statusStyles[status]}`}>
                                                    {status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        onClick={() => handleView(invoice)}
                                                        className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors"
                                                        title="View"
                                                    >
                                                        <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(invoice)}
                                                        className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(invoice)}
                                                        className="p-2.5 sm:p-1.5 text-text-muted hover:text-status-overdue rounded transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
                {hasNextPage && (
                    <div className="flex justify-center pt-2">
                        <Button variant="secondary" size="small" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                            {isFetchingNextPage ? t(KEYS.INVOICES_LOADING_MORE) : t(KEYS.INVOICES_LOAD_MORE)}
                        </Button>
                    </div>
                )}
                </>
            ) : (
                <EmptyInvoices onAdd={() => setIsAddModalOpen(true)} />
            )}

            {/* Modals */}
            <AddInvoiceModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
            />
            <EditInvoiceModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                invoice={selectedInvoice}
            />
            <DeleteInvoiceModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                invoice={selectedInvoice}
            />
            <ViewInvoiceModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                invoice={selectedInvoice}
                status={selectedInvoice ? getInvoiceStatus(selectedInvoice) : null}
            />
            {previewFileUid && (
                <FilePreviewModal
                    fileUid={previewFileUid}
                    label={previewFileLabel}
                    onClose={() => { setPreviewFileUid(null); setPreviewFileLabel(''); }}
                />
            )}
        </div>
    );
};

export default Invoices;
